// src/components/UsersModule.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import SkeletonLoader from './SkeletonLoader';
import { 
    FaEdit, FaTrash, FaUserPlus, FaSearch, 
    FaSort, FaSortUp, FaSortDown, FaBuilding, FaEnvelope, 
    FaUserTag, FaCalendarAlt, FaCheckCircle, FaTimesCircle 
} from 'react-icons/fa';

const UsersModule = () => {
    const { auth } = useContext(AuthContext);
    
    // --- STATI DATI ---
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tenants, setTenants] = useState([]); 

    // --- STATI FILTRI ---
    const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalItems: 0 });
    // filters.tenant è quello che cambia con la select
    const [filters, setFilters] = useState({ search: '', role: '', status: '', tenant: '' });
    const [sort, setSort] = useState({ col: 'created_at', order: 'DESC' });

    // --- STATI MODALE ---
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', email: '', user_role: 'user', is_active: 1, target_tenant_id: '' });
    const [message, setMessage] = useState({ text: '', type: '' });
    const [actionUser, setActionUser] = useState(null); 

    // --- FETCH DATA ---
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            // Costruzione Parametri
            const params = new URLSearchParams({
                // Parametri di contesto
                role: auth.role,
                tenant_id: auth.tenant_id, // Questo è l'ID dell'admin loggato (usato se non è superadmin)
                
                // Parametri di UI
                page: pagination.page,
                limit: pagination.limit,
                sort_by: sort.col,
                sort_order: sort.order,
                
                // Filtri
                search: filters.search,
                filter_role: filters.role,
                filter_status: filters.status,
                
                // PARAMETRO CHIAVE PER SUPERADMIN:
                // Se filters.tenant è vuoto "", l'API capirà "Tutti".
                // Se ha un ID, l'API filtrerà per quello.
                filter_tenant: filters.tenant 
            });

            const res = await axios.get(`/api_users.php?${params.toString()}`);
            
            if (res.data.success) {
                setUsers(res.data.data);
                setPagination(prev => ({
                    ...prev,
                    totalPages: res.data.pagination.total_pages,
                    totalItems: res.data.pagination.total_items
                }));
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Errore fetch users", error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [auth, pagination.page, pagination.limit, sort, filters]);

    // Carica Tenants (Solo Superadmin)
    useEffect(() => {
        if (auth.role === 'superadmin') {
            axios.get('/api_tenants.php?role=superadmin').then(res => {
                if(res.data.success) setTenants(res.data.data);
            });
        }
    }, [auth.role]);

    // Debounce Ricerca
    useEffect(() => {
        const timer = setTimeout(() => { fetchUsers(); }, 300);
        return () => clearTimeout(timer);
    }, [fetchUsers]);

    // --- HANDLERS ---
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); 
    };

    const handleSort = (column) => {
        setSort(prev => ({
            col: column,
            order: prev.col === column && prev.order === 'ASC' ? 'DESC' : 'ASC'
        }));
    };

    const renderSortIcon = (colName) => {
        if (sort.col !== colName) return <FaSort className="text-muted opacity-25 ms-1 small" />;
        return sort.order === 'ASC' ? <FaSortUp className="ms-1 small text-primary" /> : <FaSortDown className="ms-1 small text-primary" />;
    };

    // --- CRUD ---
    const openModal = (user = null) => {
        setActionUser(null);
        setMessage({ text: '', type: '' });
        if (user) {
            setFormData({
                id: user.id,
                name: user.name,
                email: user.email,
                user_role: user.role,
                is_active: user.is_active,
                target_tenant_id: user.tenant_id
            });
        } else {
            // Se creo un nuovo utente:
            // Se sono superadmin e ho un filtro tenant attivo, lo uso come default.
            // Se sono admin, uso il mio tenant.
            const defaultTargetTenant = (auth.role === 'superadmin' && filters.tenant) 
                ? filters.tenant 
                : (auth.tenant_id || '');

            setFormData({
                id: null,
                name: '',
                email: '',
                user_role: 'user',
                is_active: 1,
                target_tenant_id: defaultTargetTenant
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ text: 'Salvataggio...', type: 'info' });
        try {
            const method = formData.id ? 'put' : 'post';
            // Passiamo sia il tenant dell'admin (contesto) che il target (destinazione)
            const payload = { ...formData, role: auth.role, tenant_id: auth.tenant_id };
            
            const response = await axios[method]('/api_users.php', JSON.stringify(payload));
            
            if (response.data.success) {
                setMessage({ text: 'Operazione riuscita!', type: 'success' });
                fetchUsers();
                setTimeout(() => setShowModal(false), 1000);
            } else {
                setMessage({ text: response.data.message, type: 'danger' });
            }
        } catch (e) { setMessage({ text: 'Errore di connessione.', type: 'danger' }); }
    };

    const handleDelete = async (id) => {
        setActionUser(null);
        if(!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
        try {
            await axios.delete(`/api_users.php?id=${id}&role=${auth.role}&tenant_id=${auth.tenant_id}`);
            fetchUsers();
        } catch(e) { alert("Errore eliminazione"); }
    };

    const changePage = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    return (
        <div className="container-fluid fade-in">
            <h4 className="text-primary fw-bold mb-3">Gestione Utenti Globali</h4>

            {/* BARRA FILTRI */}
            <div className="card shadow-sm mb-4 border-0">
                <div className="card-body p-3">
                    <div className="row g-2">
                        {/* Cerca */}
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                                <input 
                                    type="text" 
                                    className="form-control border-start-0 ps-0" 
                                    placeholder="Cerca..." 
                                    name="search"
                                    value={filters.search} 
                                    onChange={handleFilterChange} 
                                />
                            </div>
                        </div>
                        
                        {/* Filtro Ruolo */}
                        <div className="col-md-2">
                            <select className="form-select" name="role" value={filters.role} onChange={handleFilterChange}>
                                <option value="">Tutti i Ruoli</option>
                                <option value="superadmin">Superadmin</option>
                                <option value="admin">Admin</option>
                                <option value="instructor">Istruttore</option>
                                <option value="user">Utente</option>
                            </select>
                        </div>

                        {/* Filtro Stato */}
                        <div className="col-md-2">
                            <select className="form-select" name="status" value={filters.status} onChange={handleFilterChange}>
                                <option value="">Tutti gli Stati</option>
                                <option value="1">Attivi</option>
                                <option value="0">Sospesi</option>
                            </select>
                        </div>

                        {/* FILTRO TENANT (VISIBILE SOLO AL SUPERADMIN) */}
                        {auth.role === 'superadmin' && (
                            <div className="col-md-3">
                                <select 
                                    className="form-select fw-bold text-primary" 
                                    name="tenant" 
                                    value={filters.tenant} 
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Tutte le Aziende</option>
                                    {tenants.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Bottone Nuovo */}
                        <div className="col text-end">
                            <button className="btn btn-success w-100 shadow-sm" onClick={() => openModal()}>
                                <FaUserPlus /> <span className="d-none d-lg-inline ms-1">Nuovo</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABELLA */}
            <div className="card shadow-sm border-0 d-none d-md-block">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0" style={{fontSize: '0.9rem'}}>
                            <thead className="bg-light text-muted">
                                <tr>
                                    <th className="ps-4 cursor-pointer" onClick={() => handleSort('name')}>Utente {renderSortIcon('name')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('role')}>Ruolo {renderSortIcon('role')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('is_active')}>Stato {renderSortIcon('is_active')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('tenant_name')}>Azienda {renderSortIcon('tenant_name')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('created_at')}>Data {renderSortIcon('created_at')}</th>
                                    <th className="text-end pe-4">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    [...Array(5)].map((_, i) => <tr key={i}><td colSpan="6" className="p-3"><SkeletonLoader /></td></tr>)
                                ) : users.length > 0 ? (
                                    users.map(user => (
                                        <tr key={user.id}>
                                            <td className="ps-4">
                                                <div className="fw-bold text-dark">{user.name}</div>
                                                <div className="small text-muted">{user.email}</div>
                                            </td>
                                            <td>
                                                <span className={`badge bg-opacity-10 text-dark border px-2 py-1 ${user.role === 'superadmin' ? 'bg-danger border-danger' : (user.role === 'admin' ? 'bg-warning border-warning' : 'bg-primary border-primary')}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td>
                                                {user.is_active == 1 
                                                    ? <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Attivo</span> 
                                                    : <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25">Sospeso</span>
                                                }
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center text-muted small">
                                                    <FaBuilding className="me-2 opacity-50"/> {user.tenant_name || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="text-muted small">
                                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="text-end pe-4">
                                                <button className="btn btn-sm btn-outline-primary me-2" onClick={()=>openModal(user)}><FaEdit /></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={()=>handleDelete(user.id)}><FaTrash /></button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="6" className="text-center py-5 text-muted">Nessun utente trovato.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Paginazione */}
                {pagination.totalPages > 1 && (
                    <div className="card-footer bg-white border-top-0 py-3">
                        <nav className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">Totale: {pagination.totalItems}</small>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => changePage(pagination.page - 1)}>Prev</button>
                                </li>
                                <li className="page-item disabled"><span className="page-link border-0">Pagina {pagination.page} di {pagination.totalPages}</span></li>
                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => changePage(pagination.page + 1)}>Next</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>

            {/* MODALE CREA/MODIFICA */}
            {showModal && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.5)', zIndex: 1070}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-warning">
                                <h5 className="modal-title">{formData.id ? 'Modifica' : 'Nuovo'} Utente</h5>
                                <button className="btn-close" onClick={()=>setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {message.text && <div className={`alert alert-${message.type}`}>{message.text}</div>}
                                <form onSubmit={handleSubmit}>
                                    {/* Select Tenant per Superadmin anche in fase di creazione */}
                                    {auth.role === 'superadmin' && (
                                        <div className="mb-3">
                                            <label className="small fw-bold">Azienda di appartenenza</label>
                                            <select className="form-select" value={formData.target_tenant_id} onChange={e=>setFormData({...formData, target_tenant_id:e.target.value})}>
                                                <option value="">Seleziona...</option>
                                                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="small fw-bold">Nome</label>
                                        <input type="text" className="form-control" required value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="small fw-bold">Email</label>
                                        <input type="email" className="form-control" required value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} />
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col">
                                            <label className="small fw-bold">Ruolo</label>
                                            <select className="form-select" value={formData.user_role} onChange={e=>setFormData({...formData, user_role:e.target.value})}>
                                                <option value="user">User</option>
                                                <option value="instructor">Instructor</option>
                                                <option value="admin">Admin</option>
                                                <option value="superadmin">Superadmin</option>
                                            </select>
                                        </div>
                                        <div className="col">
                                            <label className="small fw-bold">Stato</label>
                                            <select className="form-select" value={formData.is_active} onChange={e=>setFormData({...formData, is_active:e.target.value})}>
                                                <option value={1}>Attivo</option>
                                                <option value={0}>Sospeso</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button className="btn btn-primary w-100">Salva Utente</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersModule;